import COURSE_LEARN_TYPES from '@const/courseSubTypes'
import COURSE_TYPES from '@const/courseTypes'
import { CourseDetailed } from '@models/course'
import { MyCourseDetailed } from '@models/myCourse'
import { CourseFormValues } from './types'

type CourseAvailabilityPairInput = {
  company: { id: number; title: string }
  companyBranch?: { id: number; title: string } | null
}

type CourseData = CourseDetailed | MyCourseDetailed

const buildOptionFromEntity = <T extends { '@id': string; title: string }>(
  entity: T
) => ({
  ...entity,
  value: entity['@id'],
  label: entity.title,
})

const mapCourseAvailabilities = (
  pairs: CourseAvailabilityPairInput[] = []
): NonNullable<CourseFormValues['courseAvailabilities']> =>
  pairs.map((pair) => ({
    company: {
      id: pair.company.id,
      value: `/api/companies/${pair.company.id}`,
      label: pair.company.title,
    },
    filial: pair.companyBranch
      ? {
          id: pair.companyBranch.id,
          value: `/api/company_branches/${pair.companyBranch.id}`,
          label: pair.companyBranch.title,
        }
      : null,
  }))

const getInternalCourseFields = (
  course: MyCourseDetailed,
  result: Partial<CourseFormValues>
) => {
  result.published = course.published
  result.electricalSafetyFirstGroup = course.electricalSafetyFirstGroup
  result.sortOrder = course.sortOrder?.toString() ?? '0'

  if (course.mintrudDict) {
    result.mintrudDict = {
      value: course.mintrudDict['@id'],
      label: `${course.mintrudDict.id} ${course.mintrudDict.name}`,
    }
  }

  if (course.ownerCompany) {
    result.ownerCompany = buildOptionFromEntity(course.ownerCompany)
  }
  if (course.ownerBranch) {
    result.ownerBranch = buildOptionFromEntity(course.ownerBranch)
  }
  if (course.ownerSubdivision) {
    result.ownerSubdivision = buildOptionFromEntity(course.ownerSubdivision)
  }

  if (course.courseAvailabilities?.length) {
    result.courseAvailabilities = mapCourseAvailabilities(
      course.courseAvailabilities as CourseAvailabilityPairInput[]
    )
  }
}

const getCatalogCourseFields = (
  course: CourseDetailed,
  result: Partial<CourseFormValues>
) => {
  result.safeProgram = course.safeProgram
  result.electricalSafetyFirstGroup = course.electricalSafetyFirstGroup
  result.sortOrder = course.sortOrder?.toString() ?? '0'

  if (course.mintrudDict) {
    result.mintrudDict = {
      value: course.mintrudDict['@id'],
      label: `${course.mintrudDict.id} ${course.mintrudDict.name}`,
    }
  }

  if ((course as CourseDetailed & { courseAvailabilities?: CourseAvailabilityPairInput[] }).courseAvailabilities?.length) {
    result.courseAvailabilities = mapCourseAvailabilities(
      (course as CourseDetailed & { courseAvailabilities?: CourseAvailabilityPairInput[] })
        .courseAvailabilities || []
    )
  } else if (course.company) {
    result.courseAvailabilities = [
      {
        company: {
          id: course.company.id,
          value: `/api/companies/${course.company.id}`,
          label: course.company.title,
        },
        filial: course.companyBranch
          ? {
              id: course.companyBranch.id,
              value: `/api/company_branches/${course.companyBranch.id}`,
              label: course.companyBranch.title,
            }
          : null,
      },
    ]
  }
}

const getDefaultValues = (
  course?: CourseData,
  internalCourseMode?: boolean
): Partial<CourseFormValues> | undefined => {
  if (!course) return undefined

  const result: Partial<CourseFormValues> = {
    title: course.title,
    descriptionLink: course.descriptionLink || undefined,
    free: course.free,
  }

  if (course.hoursCount) {
    result.hoursCount = course.hoursCount.toString()
  }

  const foundType = COURSE_TYPES.find(({ value }) => value === course.type)
  if (foundType) {
    result.type = foundType
  }

  const foundSubType = COURSE_LEARN_TYPES.find(
    ({ value }) => value === course.subType
  )
  if (foundSubType) {
    result.subType = foundSubType
  }

  if (course.courseCategory) {
    result.courseCategory = {
      value: course.courseCategory['@id'],
      label: course.courseCategory.title,
    }
  }

  if (internalCourseMode) {
    getInternalCourseFields(course as MyCourseDetailed, result)
  } else {
    getCatalogCourseFields(course as CourseDetailed, result)
  }

  return result
}

export default getDefaultValues
